import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui';
import { toast } from 'react-hot-toast';
import { useDeletePurchaseOrder } from '@/hooks/usePurchaseOrder';
import { useDeleteSalesQuotation, useDeleteSalesOrder } from '@/hooks/useSalesOrder';
import { useDeleteInvoice } from '@/hooks/useInvoice';
import { useDeleteReturn } from '@/hooks/useReturns';
import { formatCurrency } from '@/services/poService';
import type { FlowStage, PSIFlowDetail } from '@/types/psiFlow';
import type { PurchaseOrder } from '@/types/purchaseorder';
import type { SalesQuotation, SalesOrder } from '@/types/salesorder';
import type { InboundShipment } from '@/types/inbound';
import type { Invoice } from '@/types/invoice';
import type { Return } from '@/types/returns';
import { FLOW_STAGE_LABELS } from '@/types/psiFlow';
import { PO_STATUS_LABELS, PO_STATUS_COLORS } from '@/types/purchaseorder';
import { SQ_STATUS_LABELS, SQ_STATUS_COLORS, SO_STATUS_LABELS, SO_STATUS_COLORS } from '@/types/salesorder';

// Union type for all document types that can be edited
type EditableDocument = PurchaseOrder | SalesQuotation | SalesOrder | InboundShipment | Invoice | Return;

interface FlowDetailDialogProps {
  stage: FlowStage;
  flowDetail: PSIFlowDetail;
  onClose: () => void;
  onEdit: (stage: FlowStage, document: EditableDocument) => void;
}

const FlowDetailDialog: React.FC<FlowDetailDialogProps> = ({
  stage,
  flowDetail,
  onClose,
  onEdit,
}) => {
  const { t, i18n } = useTranslation('psi');
  const lang = i18n.language.startsWith('zh') ? 'zh' : 'en';

  // Delete mutations
  const deletePOMutation = useDeletePurchaseOrder();
  const deleteSQMutation = useDeleteSalesQuotation();
  const deleteSOMutation = useDeleteSalesOrder();
  const deleteInvoiceMutation = useDeleteInvoice();
  const deleteReturnMutation = useDeleteReturn();

  // Get document based on stage
  const getStageDocument = () => {
    switch (stage) {
      case 'purchase_order':
        return flowDetail.purchase_order;
      case 'inbound':
        return flowDetail.inbounds?.[0];
      case 'quotation':
        return flowDetail.sales_quotation;
      case 'sales_order':
        return flowDetail.sales_order;
      case 'invoice':
        return flowDetail.invoices?.[0];
      case 'return':
        return flowDetail.returns?.[0];
      default:
        return null;
    }
  };

  const document = getStageDocument();
  const stageLabel = FLOW_STAGE_LABELS[stage]?.[lang] || stage;

  // Handle delete action
  const handleDelete = async () => {
    if (!document) return;

    if (!confirm(t('flow.dialog.confirmDelete', { stage: stageLabel }))) {
      return;
    }

    try {
      switch (stage) {
        case 'purchase_order':
          await deletePOMutation.mutateAsync((document as PurchaseOrder).po_id);
          break;
        case 'quotation':
          await deleteSQMutation.mutateAsync((document as SalesQuotation).sq_id);
          break;
        case 'sales_order':
          await deleteSOMutation.mutateAsync((document as SalesOrder).so_id);
          break;
        case 'invoice':
          await deleteInvoiceMutation.mutateAsync((document as Invoice).invoice_id);
          break;
        case 'return':
          await deleteReturnMutation.mutateAsync((document as Return).return_id);
          break;
        default:
          toast.error(t('flow.dialog.deleteNotSupported'));
          return;
      }
      toast.success(t('flow.dialog.deleteSuccess'));
      onClose();
    } catch (error) {
      toast.error(t('flow.dialog.deleteFailed', { error: error instanceof Error ? error.message : 'Unknown error' }));
    }
  };

  // Handle edit - open edit dialog via callback
  const handleEdit = () => {
    if (!document) return;
    onEdit(stage, document as EditableDocument);
  };

  // Get sheet URL from document
  const getSheetUrl = (): string | undefined => {
    switch (stage) {
      case 'purchase_order':
        return (document as PurchaseOrder)?.sheet_url;
      case 'quotation':
        return (document as SalesQuotation)?.sheet_url;
      case 'sales_order':
        return (document as SalesOrder)?.sheet_url;
      default:
        return undefined;
    }
  };

  const sheetUrl = getSheetUrl();

  // Render document info based on stage
  const renderDocumentInfo = () => {
    if (!document) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {t('flow.dialog.noDocument')}
        </div>
      );
    }

    switch (stage) {
      case 'purchase_order': {
        const po = document as PurchaseOrder;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={po.po_number} />
              <InfoField label={t('flow.dialog.status')}>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${PO_STATUS_COLORS[po.status]}`}>
                  {PO_STATUS_LABELS[po.status]}
                </span>
              </InfoField>
              <InfoField label={t('flow.dialog.vendor')} value={po.vendor_code} />
              <InfoField label={t('flow.dialog.amount')} value={formatCurrency(po.grand_total, po.currency)} />
              <InfoField label={t('flow.dialog.purchaseDate')} value={new Date(po.purchase_date).toLocaleDateString()} />
              <InfoField label={t('flow.dialog.deliveryDate')} value={new Date(po.expected_delivery).toLocaleDateString()} />
            </div>
            {po.items && po.items.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('flow.dialog.items')}</h4>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('flow.dialog.partCode')}</th>
                        <th className="px-2 py-1 text-left">{t('flow.dialog.description')}</th>
                        <th className="px-2 py-1 text-right">{t('flow.dialog.qty')}</th>
                        <th className="px-2 py-1 text-right">{t('flow.dialog.unitPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {po.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1 font-mono">{item.product_name}</td>
                          <td className="px-2 py-1 truncate max-w-[150px]">{item.specification}</td>
                          <td className="px-2 py-1 text-right">{item.quantity}</td>
                          <td className="px-2 py-1 text-right">{formatCurrency(item.unit_price, po.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'quotation': {
        const sq = document as SalesQuotation;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={sq.sq_number} />
              <InfoField label={t('flow.dialog.status')}>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${SQ_STATUS_COLORS[sq.status]}`}>
                  {SQ_STATUS_LABELS[sq.status]}
                </span>
              </InfoField>
              <InfoField label={t('flow.dialog.customer')} value={sq.customer_code} />
              <InfoField label={t('flow.dialog.amount')} value={formatCurrency(sq.subtotal, sq.currency)} />
              <InfoField label={t('flow.dialog.quoteDate')} value={new Date(sq.quotation_date).toLocaleDateString()} />
              <InfoField label={t('flow.dialog.validUntil')} value={new Date(sq.valid_until).toLocaleDateString()} />
            </div>
            {sq.items && sq.items.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('flow.dialog.items')}</h4>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('flow.dialog.partCode')}</th>
                        <th className="px-2 py-1 text-left">{t('flow.dialog.description')}</th>
                        <th className="px-2 py-1 text-right">{t('flow.dialog.qty')}</th>
                        <th className="px-2 py-1 text-right">{t('flow.dialog.unitPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {sq.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1 font-mono">{item.product_name}</td>
                          <td className="px-2 py-1 truncate max-w-[150px]">{item.specification}</td>
                          <td className="px-2 py-1 text-right">{item.quantity}</td>
                          <td className="px-2 py-1 text-right">{formatCurrency(item.unit_price, sq.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'sales_order': {
        const so = document as SalesOrder;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={so.so_number} />
              <InfoField label={t('flow.dialog.status')}>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${SO_STATUS_COLORS[so.status]}`}>
                  {SO_STATUS_LABELS[so.status]}
                </span>
              </InfoField>
              <InfoField label={t('flow.dialog.customer')} value={so.customer_code} />
              <InfoField label={t('flow.dialog.amount')} value={formatCurrency(so.grand_total, so.currency)} />
              <InfoField label={t('flow.dialog.orderDate')} value={new Date(so.order_date).toLocaleDateString()} />
              <InfoField label={t('flow.dialog.deliveryDate')} value={new Date(so.delivery_date).toLocaleDateString()} />
            </div>
          </div>
        );
      }

      case 'inbound': {
        const ib = document as InboundShipment;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={ib.inbound_number} />
              <InfoField label={t('flow.dialog.status')} value={ib.status} />
              <InfoField label={t('flow.dialog.receivedDate')} value={new Date(ib.received_date).toLocaleDateString()} />
              <InfoField label={t('flow.dialog.poNumber')} value={ib.po_number || '-'} />
            </div>
          </div>
        );
      }

      case 'invoice': {
        const inv = document as Invoice;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={inv.invoice_number} />
              <InfoField label={t('flow.dialog.status')} value={inv.status} />
              <InfoField label={t('flow.dialog.amount')} value={formatCurrency(inv.grand_total, inv.currency)} />
              <InfoField label={t('flow.dialog.invoiceDate')} value={new Date(inv.invoice_date).toLocaleDateString()} />
              <InfoField label={t('flow.dialog.dueDate')} value={inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'} />
            </div>
          </div>
        );
      }

      case 'return': {
        const ret = document as Return;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoField label={t('flow.dialog.docNumber')} value={ret.return_number} />
              <InfoField label={t('flow.dialog.status')} value={ret.status} />
              <InfoField label={t('flow.dialog.returnType')} value={ret.return_type} />
              <InfoField label={t('flow.dialog.returnDate')} value={new Date(ret.return_date).toLocaleDateString()} />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title={stageLabel}
      maxWidth="lg"
    >
      <div className="space-y-4">
        {renderDocumentInfo()}

        {/* Action Buttons */}
        {document && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('flow.dialog.edit')}
            </button>

            {sheetUrl && (
              <a
                href={sheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {t('flow.dialog.openSheet')}
              </a>
            )}

            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('flow.dialog.delete')}
            </button>

            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// Helper component for info fields
const InfoField: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({ label, value, children }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-900 dark:text-white mt-0.5">
      {children || value || '-'}
    </dd>
  </div>
);

export default FlowDetailDialog;
